/**
 * The daily home: ranked matches with scores and interested/not-interested.
 * Reads `GET /api/matches` (PIVOT-042) and shows an honest error with a retry
 * when that call fails — never fabricated or padded matches (constitution §I,
 * §VI).
 *
 * The refine chat used to sit at the bottom of this page. It moved to the
 * profile page, next to the rubric it rewrites: a chat that changes how every
 * future job is scored, with nothing on screen showing what changed, asks to be
 * trusted on the model's word alone.
 *
 * **Trap: `dry_day` and `message` are independent** (`types/domain.ts` spells
 * this out). `dry_day` means both lists are empty. `message` is the server's
 * note about today, and the most common one — "No lateral-move matches today.
 * Here are today's direct matches." — arrives *with* a full list of direct
 * cards. Gating the cards on `!dry_day` is what hid five real matches behind a
 * "nothing today" banner. So: cards come from the lists, the note comes from
 * `message`, and neither is derived from the other.
 *
 * **A dry day with `has_scores: false` means cold-start never actually ran.**
 * `POST /matches/cold-start` (`Onboarding.tsx`) is best-effort by design — a
 * failure there must not trap a candidate in onboarding — but a candidate
 * left with zero scores would otherwise sit on the generic "check back
 * tomorrow" empty state until the next daily batch happens to reach them.
 * This page retries cold-start itself, once, before showing that empty state,
 * since the endpoint upserts by `(user_id, job_id)` and is safe to repeat.
 *
 * **PIVOT-136: `GET /api/matches` is a 50-120s read, not a 50ms one**, and a
 * `useApiResource` cache keyed on `MATCHES_CACHE_KEY` is what makes the 20th
 * visit stop re-paying that in full. Rendering decides between "nothing to
 * show yet" and "showing something" on `matches` (`data`) being present, not
 * on `loading` — `loading` is `true` for the whole revalidation even on a
 * cache hit, and gating on it would flash the skeleton over content already
 * on screen. The skeleton is deliberately not `Thinking`: that component's
 * whole value is that seeing it means an LLM is working, and this route never
 * calls one.
 *
 * **PIVOT-133: the interest toggle reverts on a failed write.** It used to
 * patch optimistically and swallow the error, leaving a card claiming
 * "Marked interested" the server never recorded until a manual reload. Now
 * a failure puts the card back to its pre-click state and `interestFailed`
 * shows the error on that one card — never a page-level banner, since the
 * dashboard's real error state (`failed`, above) already means something
 * else (the whole read failed, not one write).
 *
 * **PIVOT-373: the ACCOUNT's contract picks the screen, and it comes from the
 * API.** `GET /matches/decisions` carries `contract`. A `'v1'` account sees
 * the legacy score grid and no matcher-v2 section; a `'v2'` account sees the
 * decisions and no legacy score. The two never render together, so a reader
 * can never mistake an old score for a current decision — which is exactly
 * what "old scores and v2 decisions never mix in one indistinguishable list"
 * asks for.
 *
 * The consequence is that this page waits for BOTH reads before it can pick a
 * screen, and it says so: until `contract` is known the skeleton stays up. It
 * is deliberately not "show the legacy grid while we find out" — that guess
 * would be right for today's whole population and wrong for the first v2
 * candidate, silently.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { isAbortError } from '../api/client'
import { DryDayNotice } from '../components/DryDayNotice'
import { MatchCard } from '../components/MatchCard'
import { MatcherV2Section } from '../components/MatcherV2Section'
import { Thinking, PATIENCE_MS } from '../components/Thinking'
import {
  coldStart,
  getMatcherV2Decisions,
  getMatches,
  reportGone,
  setInterest,
} from '../api/matches'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { Interest, Match, MatchesResponse } from '../types/domain'
import './Dashboard.css'

/** Shared by every `Dashboard` mount so a route change and back primes the
 * cache instead of refetching from nothing (PIVOT-136). Only this page's own
 * read uses it — `SavedSeen` and `Profile` each key their own cache under
 * their own string. */
const MATCHES_CACHE_KEY = 'dashboard-matches'

/** PIVOT-301's own cache key, separate from `MATCHES_CACHE_KEY` above — the
 * two reads (`GET /matches`, `GET /matches/decisions`) are independent and
 * must be able to revalidate on their own schedules. */
const MATCHER_V2_CACHE_KEY = 'dashboard-matcher-v2-decisions'

/** Six blocks, always — a fixed, made-up number rather than one derived from
 * a real count, so the skeleton never implies how many matches today has
 * (PIVOT-136: "a small dishonesty" the ticket explicitly calls out). */
const SKELETON_BLOCK_KEYS = [0, 1, 2, 3, 4, 5]

function patchInterest(list: Match[], jobId: string, interest: Interest): Match[] {
  return list.map((m) => (m.job.id === jobId ? { ...m, interest } : m))
}

function findInterest(matches: MatchesResponse, jobId: string): Interest | undefined {
  return (
    matches.direct.find((m) => m.job.id === jobId)?.interest ??
    matches.lateral.find((m) => m.job.id === jobId)?.interest
  )
}

export default function Dashboard() {
  const { t } = useI18n()
  const {
    data: matches,
    failed,
    reload: load,
    setData: setMatches,
  } = useApiResource(getMatches, { cacheKey: MATCHES_CACHE_KEY })

  // PIVOT-301: a second, independent read alongside `matches` above — see
  // `MatcherV2Section`'s own docstring for why a `409 check_changed` on a
  // card's feedback action reloads THIS resource, never `matches`.
  const {
    data: matcherV2Decisions,
    failed: decisionsFailed,
    reload: reloadMatcherV2Decisions,
  } = useApiResource(getMatcherV2Decisions, { cacheKey: MATCHER_V2_CACHE_KEY })

  // PIVOT-373: `null` means "we do not know yet", never "legacy". Every
  // branch below that picks a surface reads THIS, so an unknown contract
  // renders the skeleton rather than a guessed screen.
  const contract = matcherV2Decisions?.contract ?? null
  const showLegacy = contract === 'v1'

  // Whether the wait has gone on long enough to admit it (mirrors
  // `Thinking`'s `PATIENCE_MS` rather than a second invented number). Only
  // meaningful while there is nothing cached to show yet — a revalidation
  // behind already-visible content never needs to say anything.
  const [patient, setPatient] = useState(false)
  useEffect(() => {
    if (matches) return
    setPatient(false)
    const timer = setTimeout(() => setPatient(true), PATIENCE_MS)
    return () => clearTimeout(timer)
  }, [matches])

  // Retries a cold-start that silently failed at the end of onboarding,
  // rather than showing the same empty state a genuine dry day gets. Only
  // once: if cold-start runs and there is still nothing, that is an honest
  // dry day (e.g. every live job was filtered out), not a bug to keep retrying.
  // `retried` is state, not a ref: the retry's completion has to reliably
  // produce a render that shows the honest empty state once it is done.
  const [retrying, setRetrying] = useState(false)
  const [retried, setRetried] = useState(false)

  // PIVOT-133: the retry is a request this page starts, so this page cancels
  // it (frontend/CLAUDE.md §7). The controller sits in a ref with its own
  // mount-scoped cleanup rather than in the effect below, because that effect
  // re-runs the moment `setRetried(true)` lands — a cleanup there would abort
  // the cold start one render after starting it.
  const coldStartAbort = useRef<AbortController | null>(null)
  useEffect(() => () => coldStartAbort.current?.abort(), [])

  useEffect(() => {
    // PIVOT-373: only a legacy account's empty slate is a cold-start problem
    // worth retrying. A v2 account's legacy list is empty by design, and
    // retrying for them would spend a run on a surface they never see.
    if (!showLegacy) return
    if (!matches?.dry_day || matches.has_scores || retried) return
    setRetried(true)
    setRetrying(true)
    const controller = new AbortController()
    coldStartAbort.current = controller
    void (async () => {
      try {
        await coldStart(controller.signal)
      } catch (reason) {
        // A cancelled retry stops here, silently: the page that asked for it
        // is gone, so re-reading matches for it is work nobody sees. Any
        // other failure falls through to the re-read, which is what puts an
        // honest empty state on screen instead of the "preparing" one.
        if (isAbortError(reason)) return
      }
      await load()
      setRetrying(false)
    })()
  }, [matches, retried, load, showLegacy])

  // Suppressed until the retry above has had its one attempt, so a candidate
  // whose cold-start is about to be retried never sees "check back tomorrow"
  // flash before the retry even starts.
  const showDryDay = matches?.dry_day && (matches.has_scores || retried)

  // Jobs whose last interest write failed — cleared the moment a fresh
  // attempt starts, so a successful retry removes the error itself rather
  // than needing a second effect to notice the write succeeded.
  const [interestErrors, setInterestErrors] = useState<ReadonlySet<string>>(new Set())

  // PIVOT-166: "this job no longer exists". `reportedGone` is optimistic and
  // one-way for this session (there is nothing to revert *to* — the button
  // just reads as sent); `reportGoneErrors` mirrors `interestErrors`'s
  // revert-on-failure shape exactly.
  const [reportedGone, setReportedGone] = useState<ReadonlySet<string>>(new Set())
  const [reportGoneErrors, setReportGoneErrors] = useState<ReadonlySet<string>>(new Set())

  const handleReportGone = async (jobId: string) => {
    setReportGoneErrors((prev) => {
      if (!prev.has(jobId)) return prev
      const next = new Set(prev)
      next.delete(jobId)
      return next
    })
    setReportedGone((prev) => new Set(prev).add(jobId))
    try {
      await reportGone(jobId)
    } catch (reason) {
      if (isAbortError(reason)) return
      setReportedGone((prev) => {
        const next = new Set(prev)
        next.delete(jobId)
        return next
      })
      setReportGoneErrors((prev) => new Set(prev).add(jobId))
    }
  }

  const handleInterest = async (jobId: string, interest: Interest) => {
    const previous = matches && findInterest(matches, jobId)
    setInterestErrors((prev) => {
      if (!prev.has(jobId)) return prev
      const next = new Set(prev)
      next.delete(jobId)
      return next
    })
    setMatches((prev) =>
      prev
        ? {
            ...prev,
            direct: patchInterest(prev.direct, jobId, interest),
            lateral: patchInterest(prev.lateral, jobId, interest),
          }
        : prev,
    )
    try {
      await setInterest(jobId, interest)
    } catch (reason) {
      // A cancelled request (unmount) is the intended outcome, not a failure
      // to reconcile — the component reverting is moot if it no longer exists.
      if (isAbortError(reason)) return
      // Revert: the write failed, so the card must not keep claiming an
      // interest the server never recorded (frontend/CLAUDE.md §7).
      if (previous) {
        setMatches((prev) =>
          prev
            ? {
                ...prev,
                direct: patchInterest(prev.direct, jobId, previous),
                lateral: patchInterest(prev.lateral, jobId, previous),
              }
            : prev,
        )
      }
      setInterestErrors((prev) => new Set(prev).add(jobId))
    }
  }

  return (
    <div className="dashboard-page">
      <title>{`${t('dashboard.title')} — ${t('common.appName')}`}</title>
      {/* PIVOT-126: title left, the refine-criteria link right, one row
          (`.page-header`, `styles/layout.css`) — it's a page-level action
          like Stripe's "Add funds", not page-end content, so it moved up
          from the footer-style link that used to sit below the grid. */}
      <div className="page-header">
        <div>
          <h1>{t('dashboard.title')}</h1>
          {/* PIVOT-142: "the day's status left" (plan-frontend-redesign-
              2026-08-07.md §3b) — the title half of that already existed,
              the count did not. `direct.length + lateral.length` rather than
              a second server field: the page already has both arrays in
              full, so this can only ever agree with what's rendered below
              it. Waits for `matches` rather than showing "0" during the
              skeleton, which is not a real count yet. */}
          {showLegacy && matches && (
            <p className="eyebrow dashboard-count">
              {t('dashboard.matchCount', {
                count: matches.direct.length + matches.lateral.length,
              })}
            </p>
          )}
        </div>
        <div className="page-header-actions">
          <Link className="dashboard-refine-link" to="/profile#refine">
            {t('dashboard.refineLink')}
          </Link>
        </div>
      </div>

      {/* Nothing cached and nothing has landed yet — the skeleton, not a bare
          "Loading…" paragraph (PIVOT-136). `matches` is always set by the
          time `retrying` can be true (the retry effect below requires it), so
          this and the `Thinking` step just below are mutually exclusive. */}
      {(!matches || contract === null) && (
        // `<output>` carries an implicit `status` role, so this announces the
        // caption swap at `PATIENCE_MS` without a redundant explicit `role`
        // (oxlint's `jsx-a11y/prefer-tag-over-role`).
        <output className="dashboard-skeleton-wrap" aria-live="polite">
          <p className="dashboard-skeleton-caption">
            {t(patient ? 'dashboard.loadingPatience' : 'common.loading')}
          </p>
          <div className="match-grid dashboard-skeleton" aria-hidden="true">
            {SKELETON_BLOCK_KEYS.map((key) => (
              <div key={key} className="dashboard-skeleton-block" />
            ))}
          </div>
        </output>
      )}

      {/* At least as prominent as the content it replaces (PIVOT-136) — a
          stale cached grid still renders below this unconditionally, so a
          failed revalidation shows both the old data and the honest failure,
          never one silently standing in for the other. */}
      {(failed || decisionsFailed) && (
        <div className="dashboard-error">
          <p>{t('common.error')}</p>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              // Both reads decide what this page renders, so the retry
              // restarts both rather than leaving the screen undecidable.
              void load()
              void reloadMatcherV2Decisions()
            }}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* Cold-start never produced a score for this profile — retrying it
          before telling the candidate there is nothing today. */}
      {showLegacy && retrying && (
        <Thinking
          wide
          label="dashboard.preparingMatches"
          patience="dashboard.preparingMatchesPatience"
        />
      )}

      {/* Nothing at all today: the notice carries the server's sentence as its
          body, so the message is still rendered verbatim on this branch. */}
      {showLegacy && matches && showDryDay && <DryDayNotice message={matches.message} />}

      {/* Something today, and the server had something to say about it. */}
      {showLegacy && matches && !matches.dry_day && matches.message && (
        <p className="dashboard-note">{matches.message}</p>
      )}

      {showLegacy && matches && matches.lateral.length > 0 && (
        <section>
          <h2>{t('dashboard.lateralTitle')}</h2>
          <div className="match-grid">
            {/* PIVOT-142: `featured` on the top-ranked card of each list —
                the lists arrive pre-ranked from the backend, so index 0 is
                already "top-ranked"; nothing here re-sorts or re-derives it. */}
            {matches.lateral.map((m, i) => (
              <MatchCard
                key={m.id}
                match={m}
                onInterest={handleInterest}
                onReportGone={handleReportGone}
                interestFailed={interestErrors.has(m.job.id)}
                reportedGone={reportedGone.has(m.job.id)}
                reportGoneFailed={reportGoneErrors.has(m.job.id)}
                featured={i === 0}
              />
            ))}
          </div>
        </section>
      )}
      {showLegacy && matches && matches.direct.length > 0 && (
        <section>
          <h2>{t('dashboard.directTitle')}</h2>
          <div className="match-grid">
            {matches.direct.map((m, i) => (
              <MatchCard
                key={m.id}
                match={m}
                onInterest={handleInterest}
                onReportGone={handleReportGone}
                interestFailed={interestErrors.has(m.job.id)}
                reportedGone={reportedGone.has(m.job.id)}
                reportGoneFailed={reportGoneErrors.has(m.job.id)}
                featured={i === 0}
              />
            ))}
          </div>
        </section>
      )}

      {/* PIVOT-301/PIVOT-373: the matcher-v2 surface. It renders only for a
          v2 account and reads that from `decisions.contract` itself, so this
          page passes no gate prop — the ambiguity the old `pendingAlone`
          prop worked around no longer exists on the wire. */}
      <MatcherV2Section
        decisions={matcherV2Decisions}
        onStale={() => void reloadMatcherV2Decisions()}
      />
    </div>
  )
}
